import { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import styled from 'styled-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { t } from 'localization/utils/t';
import { useRouter } from 'next/router';

import { AUCTION_COLLECTION_PAGE_REDIRECT } from 'collections/enums/AuctionCollectionPageRedirect';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { COLLECTION_PAGE_REDIRECTS } from 'collections/enums/CollectionPageRedirects';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { getSearchStateFromUrl } from 'search/utils/getSearchStateFromUrl';
import { ISubCollectionList } from 'shared/types/ConsumerSearch';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { linkCollectionDescriptionToGiftCard } from 'shared/utils/linkCollectionDescriptionToGiftCard';
import { logger } from 'server/utils/logger';
import { requestService } from 'shared/utils/services/requestService';
import { RESULTS_PER_PAGE } from 'search/enums/constants';
import { SearchGrid } from 'search/components/searchGrid/SearchGrid';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useConstructorUserParams } from 'shared/hooks/useConstructorUserParams';

const TP = 'pages.collections.slug';

dayjs.extend(utc);
dayjs.extend(timezone);

const removeDuplicateSubCollections = (subCollections) => {
  const res = [];
  for (const subCollection of subCollections) {
    if (res.findIndex((sc) => sc.collectionSlug === subCollection.collectionSlug) === -1) {
      res.push(subCollection);
    }
  }
  return res;
};

/* eslint-disable camelcase  */
interface ICollectionProps {
  constructorResponse: {
    request?: any;
    response?: any;
    result_id?: string;
  };
  globalPricingId: string;
  isCrawler?: boolean;
}

interface ICollection {
  display_name: string;
  id: string;
  data: {
    description: string;
    is_no_index: boolean;
    title: string;
    metadata_description: string;
    header_asset?: { url: string; type: string };
  };
}
/* eslint-enable camelcase */

const Collection = ({
  constructorResponse,
  globalPricingId,
  isCrawler = false,
}: ICollectionProps) => {
  const [constructorRes, setConstructorResponse] = useState(constructorResponse);
  const constructorUserParams = useConstructorUserParams();
  const ConstructorClient = useConstructorClient();

  const [subCollections, setSubCollections] = useState<ISubCollectionList>([]);
  const querySlug = useRef<string[] | null>(null);

  const router = useRouter();
  const { query, pathname } = router;
  const isSubCollectionEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_COLLECTION_IN_COLLECTION,
  );
  const { collection } = constructorRes.response || constructorResponse.response;

  const { display_name: displayName, data }: ICollection = collection;
  const collectionId = query?.slug?.[1] || query?.slug?.[0];

  const metaImage = data?.header_asset?.type === 'picture' && data?.header_asset?.url;
  const description = linkCollectionDescriptionToGiftCard(data?.description);

  const { pageTitle, pageDescription } = usePageMetadata({
    defaultTitle: t(`${TP}.metaTitle`, '{name} Collection ', {
      name: displayName,
    }),
    mainTitle: data?.title || '',
    mainDescription: data?.metadata_description || '',
    constructorResponse,
  });

  useEffect(() => {
    const fetchSubCollections = async () => {
      try {
        const res = await requestService({
          url: `/web-api/consumer-search/get-sub-collections?slug=${query.slug[0]}`,
          method: 'get',
        });

        setSubCollections(removeDuplicateSubCollections(res?.subCollectionsList || []));

        if (res?.subCollectionsList) querySlug.current = query.slug as string[];
      } catch {
        setSubCollections([]);
      }
    };

    const fetchConstructorResponse = async () => {
      try {
        const searchState = getSearchStateFromUrl(query, pathname);
        const _constructorResponse = await ConstructorClient.browse.getBrowseResults(
          'collection_id',
          query?.slug?.[1] || query?.slug?.[0],
          {
            resultsPerPage: RESULTS_PER_PAGE,
            ...searchState,
            fmtOptions: { groups_max_depth: 5 },
          },
          constructorUserParams,
        );

        // If there are no responses, remove search filters
        if (_constructorResponse?.response) {
          if (_constructorResponse?.response?.total_num_results > 0) {
            setConstructorResponse(_constructorResponse);
            querySlug.current = query.slug as string[];
          } else {
            void router.replace(`/collections/${query?.slug[0]}/${query?.slug[1]}`, undefined, {
              shallow: true,
            });
          }
        }
      } catch {
        setConstructorResponse({
          response: { results: [], total_num_results: 0 },
        });
      }
    };

    // This is to prevent refetches when any filters are applied
    if (isSubCollectionEnabled && querySlug.current?.[0] !== query.slug[0])
      void fetchSubCollections();

    // When sub collection changes, refetch contructor response on client as we don't call getServerSideProps
    if (isSubCollectionEnabled && querySlug.current && querySlug.current?.[1] !== query.slug[1])
      void fetchConstructorResponse();

    setConstructorResponse(constructorResponse);
  }, [query.slug]);

  return (
    <Collection.Container>
      <Head>
        <title key="title">{pageTitle}</title>
        <meta property="og:title" key="og:title" content={pageTitle} />
        <meta
          name="description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {name} now on GOAT. Authenticity Assured.', {
              name: data?.title || displayName,
            })
          }
          key="description"
        />
        <meta
          property="og:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {name} now on GOAT. Authenticity Assured.', {
              name: data?.title || displayName,
            })
          }
          key="og:description"
        />
        {metaImage && (
          <>
            <meta name="twitter:image" content={metaImage} key="twitter:image" />
            <meta property="og:image" content={metaImage} key="og:image" />
          </>
        )}
      </Head>
      <SearchGrid
        browseKey="collection_id"
        browseValue={collectionId}
        constructorResponse={constructorRes || constructorResponse}
        description={description}
        globalPricingId={globalPricingId}
        noIndex={data?.is_no_index}
        title={data?.title || displayName}
        isCrawler={isCrawler}
        subCollections={subCollections}
      />
    </Collection.Container>
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { slug: slugList }: { slug: string[] } = query;

  const collectionSlug = slugList[0];
  const subCollectionSlug = slugList[1];

  const isCrawler = isSearchCrawler(req?.headers);
  try {
    // For better SEO, we are redirecting (301) some duplicate collections
    if (COLLECTION_PAGE_REDIRECTS[collectionSlug]) {
      return {
        redirect: {
          statusCode: 301,
          destination: getRedirectionPath(COLLECTION_PAGE_REDIRECTS[collectionSlug], locale),
        },
      };
    }
    const globalPricingId = await fetchGlobalPricingId({ req, res });
    // Check if collection for this slug exists, if so return collection
    const constructorResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'collection_id',
      filterValue: subCollectionSlug || collectionSlug,
    });

    const invalidSearch =
      !constructorResponse.response || constructorResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    const auctionEndTime = constructorResponse?.response?.collection?.data?.auctionEndTime;
    const endDate = dayjs(auctionEndTime).tz('America/Los_Angeles').toDate();
    const now = dayjs().tz('America/Los_Angeles').toDate();

    const isAuctionOver = dayjs(now).isAfter(endDate);

    if (auctionEndTime && isAuctionOver && AUCTION_COLLECTION_PAGE_REDIRECT[collectionSlug]) {
      return {
        redirect: {
          destination: getRedirectionPath(AUCTION_COLLECTION_PAGE_REDIRECT[collectionSlug], locale),
          permanent: false,
        },
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        constructorResponse,
        globalPricingId,
        isCrawler,
      },
    };
  } catch (error) {
    bugsnag(error, {
      context: 'Fetching collection',
      metadata: {
        resolvedUrl,
        utm_source: req?.query?.utm_source,
        utm_campain: req?.query?.utm_campain,
        utm_content: req?.query?.utm_content,
        referer: req?.headers?.referer,
      },
    });
    logger.error(error, 'Fetching collection failed');
    return {
      notFound: true,
    };
  }
};

Collection.Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  position: relative;
`;

export default Collection;
